import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { INewsFeedData, NewsFeedComponent } from '@fi-app/news-feed/news-feed/news-feed.component';
import { ForumService } from '@fi-app/forum/forum.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { NewsFeedService } from '@fi-app/news-feed/news-feed.service';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { BehaviorSubject, Subject } from 'rxjs';
import { WidgetPlaceHolderControlModel } from '@fi-shared/widget/widget-place-holder-control.model';
import { takeUntil } from 'rxjs/operators';
import { WidgetSharedService } from '@fi-shared/widget/widget-shared.service';
import { FiBackendRequestType, FiRequestsQueueService } from '@fi-shared/fi-shared-services/fi-requests-queue.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { INewsFeedModel } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';

@Component({
    selector: 'fi-widget-content-forum-feed',
    templateUrl: './forum-feed.component.html',
    styleUrls: ['./forum-feed.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-widget-content-forum-feed' },
    standalone: false,
})
export class WidgetContentForumFeedComponent implements OnInit, OnDestroy {
    @Input() control: WidgetPlaceHolderControlModel;

    @ViewChild(NewsFeedComponent, { static: false }) newsFeedComponent: NewsFeedComponent;

    hasAccessToModule: boolean;
    initialNewsFeedResponse: INewsFeedModel[];
    newsFeedData: INewsFeedData;

    private _isEmpty$ = new BehaviorSubject<boolean>(true);
    get isEmpty$() {
        return this._isEmpty$.asObservable();
    }

    constructor(
        private forumService: ForumService,
        private fiLoadingService: LoadingService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private newsFeedService: NewsFeedService,
        private widgetSharedService: WidgetSharedService,
        private fiRequestsQueueService: FiRequestsQueueService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.hasAccessToModule = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.Forum,
            AccessRightType.Share,
        );

        if (this.hasAccessToModule) {
            setTimeout(() => this.initFeed(), FiAppSettings.StartDelay);
        }

        this.widgetSharedService
            .getWidgetSettingsChanged$(() => this.control.Settings)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((data) => {
                this.cdr.markForCheck();
                if (!data || !data.skipReloadData) {
                    this.initFeed();
                }
            });
    }

    private initFeed() {
        this.newsFeedData = {
            forumId: this.control.Settings.WidgetForumId || -1,
            newsFeedHeaderText: '',
            newsFeedHeaderSref: FiStateNames.Forum.View,
            newsFeedHeaderSrefParams: { id: this.control.Settings.WidgetForumId },
            NumberOfRecordsToTake: this.control.Settings.NumberOfRecordsToTake,
        };

        this.fiRequestsQueueService.addRequestToQueue(() => {
            const promise = this.newsFeedService
                .getForumFeedData(this.newsFeedData.forumId, 0, this.control.Settings.NumberOfRecordsToTake || 5)
                .then((initialNewsFeedResponse) => {
                    if (!initialNewsFeedResponse || initialNewsFeedResponse.data.length === 0) {
                        this.fiLoadingService.finishLoading();
                    }
                    if (!initialNewsFeedResponse) {
                        return;
                    }

                    this.newsFeedData.newsFeedHeaderText = initialNewsFeedResponse.forumName;

                    this.initialNewsFeedResponse = initialNewsFeedResponse.data;

                    this._isEmpty$.next(false);
                });

            return promise;
        }, FiBackendRequestType.WidgetForumFeed);
    }

    onInitialRenderDone() {
        this.fiLoadingService.finishLoading();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
